import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    WrenchScrewdriverIcon,
    LifebuoyIcon,
    MegaphoneIcon,
    PhotoIcon,
    XMarkIcon,
    ArrowPathIcon,
    PaperAirplaneIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon,
    BellIcon,
    InformationCircleIcon,
} from "@heroicons/react/24/outline";
import postNotifications from "../api/postNotification";
import { uploadChatFile } from "../api/operator";
import LangTabs, { CONTENT_LANGS } from "../components/LangTabs";

const TYPES = [
    { value: "service", label: "Сервис", hint: "Обновления и работа сервиса", icon: WrenchScrewdriverIcon, tone: "text-sky-500 bg-sky-500/15" },
    { value: "support", label: "Поддержка", hint: "Советы и помощь пользователям", icon: LifebuoyIcon, tone: "text-emerald-500 bg-emerald-500/15" },
    { value: "promotions", label: "Акции", hint: "Скидки, конкурсы, предложения", icon: MegaphoneIcon, tone: "text-amber-500 bg-amber-500/15" },
];

// Push-баннер на телефоне обрезает длинный текст — подсказываем, где граница
const TITLE_PUSH_LIMIT = 65;
const TEXT_PUSH_LIMIT = 180;
const MAX_IMAGE_MB = 10;
const EMPTY_TR = { kk: { title: "", text: "" }, en: { title: "", text: "" } };
const EMPTY = { title: "", text: "", notificationType: "service", tr: EMPTY_TR };

/** Перевод заполнен полностью / частично / пуст */
const trStatus = (t) => (t.title.trim() && t.text.trim() ? "filled" : t.title.trim() || t.text.trim() ? "partial" : "empty");

const inputCls =
    "w-full rounded-lg border border-border-primary bg-bg-primary px-3 py-2.5 text-sm text-text-primary " +
    "placeholder:text-text-muted focus:border-[#4F45E5] focus:outline-none focus:ring-2 focus:ring-[#4F45E5]/30";

const Counter = ({ value, limit }) => (
    <span className={`text-xs tabular-nums ${value > limit ? "text-amber-500" : "text-text-muted"}`}>
        {value}
        {value > limit ? ` · в push обрежется после ${limit}` : ` / ${limit}`}
    </span>
);

function AddNotificationForm() {
    const navigate = useNavigate();
    const [form, setForm] = useState(EMPTY);
    const [image, setImage] = useState(null); // { name, preview, url, uploading, error }
    const [dragOver, setDragOver] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState(null);
    const [sent, setSent] = useState(null);
    const [lang, setLang] = useState("ru");
    const fileInputRef = useRef(null);

    useEffect(() => () => image?.preview && URL.revokeObjectURL(image.preview), [image?.preview]);

    const set = (key) => (e) => {
        setForm((f) => ({ ...f, [key]: e.target.value }));
        setConfirming(false);
        setError(null);
    };
    // Заголовок/текст выбранного языка (RU — основные поля, KZ/EN — переводы)
    const fieldValue = (key) => (lang === "ru" ? form[key] : form.tr[lang][key]);
    const setField = (key) => (e) => {
        const value = e.target.value;
        setForm((f) => (lang === "ru" ? { ...f, [key]: value } : { ...f, tr: { ...f.tr, [lang]: { ...f.tr[lang], [key]: value } } }));
        setConfirming(false);
        setError(null);
    };
    const status = { ru: form.title.trim() && form.text.trim() ? "filled" : "partial", kk: trStatus(form.tr.kk), en: trStatus(form.tr.en) };
    const partialLang = ["kk", "en"].find((l) => status[l] === "partial");

    const attachImage = async (file) => {
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            setError("Можно прикрепить только изображение (JPG, PNG, WebP).");
            return;
        }
        if (file.size > MAX_IMAGE_MB * 1024 * 1024) {
            setError(`Изображение больше ${MAX_IMAGE_MB} МБ.`);
            return;
        }
        const preview = URL.createObjectURL(file);
        setError(null);
        setConfirming(false);
        setImage({ name: file.name, preview, url: null, uploading: true, error: null });
        try {
            // Картинка хранится ссылкой (не base64 в базе) — mobile и web умеют показывать URL
            const url = await uploadChatFile(file);
            setImage((cur) => (cur?.preview === preview ? { ...cur, url, uploading: false } : cur));
        } catch (err) {
            console.error("Image upload failed:", err);
            setImage((cur) => (cur?.preview === preview ? { ...cur, uploading: false, error: err.message || "Ошибка загрузки" } : cur));
        }
    };

    const removeImage = () => {
        setImage(null);
        setConfirming(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const title = form.title.trim();
    const text = form.text.trim();
    const canSend = title && text && !partialLang && !image?.uploading && !image?.error && !sending;
    // Предпросмотр — на выбранном языке; пустой перевод → пользователь увидит RU
    const previewTitle = (lang !== "ru" && status[lang] === "filled" ? form.tr[lang].title.trim() : title) || "Заголовок уведомления";
    const previewText = (lang !== "ru" && status[lang] === "filled" ? form.tr[lang].text.trim() : text) || "Текст уведомления";

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!canSend) {
            if (!title || !text) setError("Заполните заголовок и текст на русском (RU) — это обязательный язык.");
            else if (partialLang) setError(`${partialLang === "kk" ? "KZ" : "EN"}: заполните и заголовок, и текст — или оставьте оба пустыми.`);
            return;
        }
        if (!confirming) {
            setConfirming(true);
            return;
        }
        setSending(true);
        setError(null);
        try {
            const translations = Object.fromEntries(
                ["kk", "en"].filter((l) => status[l] === "filled").map((l) => [l, { title: form.tr[l].title.trim(), text: form.tr[l].text.trim() }])
            );
            await postNotifications(title, text, image?.url || null, form.notificationType, translations);
            setSent({ title, type: form.notificationType });
            setForm(EMPTY);
            setLang("ru");
            removeImage();
        } catch (err) {
            console.error("Error sending notification:", err);
            setError(err.message || "Не удалось отправить уведомление.");
        } finally {
            setSending(false);
            setConfirming(false);
        }
    };

    const typeMeta = TYPES.find((t) => t.value === form.notificationType);

    if (sent) {
        return (
            <div className="mx-auto max-w-lg rounded-block border border-border-primary bg-bg-card p-8 text-center shadow-sm">
                <CheckCircleIcon className="mx-auto h-14 w-14 text-emerald-500" />
                <h2 className="mt-3 text-xl font-bold text-text-primary">Уведомление отправлено</h2>
                <p className="mt-1 text-sm text-text-secondary">
                    «{sent.title}» появилось в разделе «{TYPES.find((t) => t.value === sent.type)?.label}» у всех пользователей, push-уведомления разосланы.
                </p>
                <div className="mt-6 flex flex-col justify-center gap-2 sm:flex-row">
                    <button
                        type="button"
                        onClick={() => setSent(null)}
                        className="rounded-lg bg-accent-grad px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
                    >
                        Создать ещё
                    </button>
                    <button
                        type="button"
                        onClick={() => navigate("/manage-notifications")}
                        className="rounded-lg border border-border-primary px-4 py-2 text-sm font-medium text-text-secondary hover:bg-bg-tertiary"
                    >
                        К списку уведомлений
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-4 text-left">
            <div>
                <h2 className="text-2xl font-bold text-text-primary">Новое уведомление</h2>
                <p className="mt-0.5 text-sm text-text-secondary">
                    Появится в разделе «Уведомления» в приложении и на сайте и придёт push-уведомлением всем пользователям.
                </p>
            </div>

            <div className="flex items-start gap-2 rounded-lg border border-border-primary bg-bg-card px-4 py-2.5 text-xs text-text-secondary">
                <InformationCircleIcon className="h-4 w-4 shrink-0 text-text-muted" />
                <span>
                    Нужна рассылка по региону, типу пользователей или языку со статистикой просмотров?{" "}
                    <button type="button" onClick={() => navigate("/marketing-hub")} className="font-medium text-[#7C3AED] hover:underline dark:text-indigo-300">
                        Используйте «Маркетинг»
                    </button>
                    .
                </span>
            </div>

            <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-5">
                <form onSubmit={handleSubmit} noValidate className="space-y-5 rounded-block border border-border-primary bg-bg-card p-5 shadow-sm xl:col-span-3">
                    {/* Type */}
                    <fieldset>
                        <legend className="mb-2 text-sm font-medium text-text-primary">Раздел</legend>
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                            {TYPES.map((t) => {
                                const Icon = t.icon;
                                const active = form.notificationType === t.value;
                                return (
                                    <button
                                        key={t.value}
                                        type="button"
                                        aria-pressed={active}
                                        onClick={() => set("notificationType")({ target: { value: t.value } })}
                                        className={`flex items-center gap-3 rounded-lg border p-3 text-left transition-colors ${
                                            active ? "border-[#4F45E5] bg-[#4F45E5]/10 ring-1 ring-[#4F45E5]/40" : "border-border-primary hover:bg-bg-tertiary"
                                        }`}
                                    >
                                        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${t.tone}`}>
                                            <Icon className="h-5 w-5" />
                                        </span>
                                        <span className="min-w-0">
                                            <span className="block text-sm font-semibold text-text-primary">{t.label}</span>
                                            <span className="block text-xs text-text-secondary">{t.hint}</span>
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </fieldset>

                    {/* Language */}
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <LangTabs value={lang} onChange={setLang} status={status} />
                        <span className="text-xs text-text-muted">
                            {lang === "ru" ? "Пользователь видит текст на языке своего устройства" : `Пусто — пользователи с ${CONTENT_LANGS.find((l) => l.id === lang).name} увидят RU`}
                        </span>
                    </div>

                    {/* Title */}
                    <label className="block">
                        <span className="mb-1.5 flex items-baseline justify-between text-sm font-medium text-text-primary">
                            Заголовок <span className="font-normal text-text-muted">· {lang === "kk" ? "KZ" : lang.toUpperCase()}</span>
                            <Counter value={fieldValue("title").length} limit={TITLE_PUSH_LIMIT} />
                        </span>
                        <input
                            type="text"
                            autoFocus
                            value={fieldValue("title")}
                            onChange={setField("title")}
                            placeholder={lang === "ru" ? "Например: Новые категории в каталоге" : lang === "kk" ? "Мысалы: Каталогта жаңа санаттар" : "E.g.: New categories in the catalog"}
                            className={inputCls}
                        />
                    </label>

                    {/* Text */}
                    <label className="block">
                        <span className="mb-1.5 flex items-baseline justify-between text-sm font-medium text-text-primary">
                            Текст <span className="font-normal text-text-muted">· {lang === "kk" ? "KZ" : lang.toUpperCase()}</span>
                            <Counter value={fieldValue("text").length} limit={TEXT_PUSH_LIMIT} />
                        </span>
                        <textarea
                            rows={6}
                            value={fieldValue("text")}
                            onChange={setField("text")}
                            onKeyDown={(e) => (e.ctrlKey || e.metaKey) && e.key === "Enter" && handleSubmit(e)}
                            onPaste={(e) => {
                                const file = [...(e.clipboardData?.files || [])].find((f) => f.type.startsWith("image/"));
                                if (file) {
                                    e.preventDefault();
                                    attachImage(file);
                                }
                            }}
                            placeholder="Полный текст уведомления. В разделе «Уведомления» показывается целиком."
                            className={`${inputCls} resize-y`}
                        />
                    </label>

                    {/* Image */}
                    <div>
                        <span className="mb-1.5 block text-sm font-medium text-text-primary">
                            Изображение <span className="font-normal text-text-muted">— необязательно</span>
                        </span>
                        <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => attachImage(e.target.files?.[0])} />
                        {image ? (
                            <div className="flex items-center gap-3 rounded-lg border border-border-primary bg-bg-primary p-2">
                                <img src={image.preview} alt="" className="h-16 w-24 shrink-0 rounded-md object-cover" />
                                <div className="min-w-0 flex-1 text-sm">
                                    <p className="truncate font-medium text-text-primary">{image.name}</p>
                                    {image.uploading && (
                                        <p className="flex items-center gap-1.5 text-xs text-text-secondary">
                                            <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" /> Загрузка…
                                        </p>
                                    )}
                                    {image.url && <p className="text-xs text-emerald-500">Загружено</p>}
                                    {image.error && (
                                        <p className="text-xs text-rose-500">
                                            {image.error} ·{" "}
                                            <button type="button" onClick={() => fileInputRef.current?.click()} className="underline">
                                                выбрать другое
                                            </button>
                                        </p>
                                    )}
                                </div>
                                <button type="button" onClick={removeImage} aria-label="Убрать изображение" className="rounded-lg p-2 text-text-muted hover:bg-rose-500/10 hover:text-rose-500">
                                    <XMarkIcon className="h-4 w-4" />
                                </button>
                            </div>
                        ) : (
                            <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                onDragOver={(e) => {
                                    e.preventDefault();
                                    setDragOver(true);
                                }}
                                onDragLeave={() => setDragOver(false)}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    setDragOver(false);
                                    attachImage(e.dataTransfer.files?.[0]);
                                }}
                                className={`flex w-full items-center justify-center gap-2 rounded-lg border border-dashed px-4 py-5 text-sm transition-colors ${
                                    dragOver ? "border-[#4F45E5] bg-[#4F45E5]/10 text-text-primary" : "border-border-primary text-text-secondary hover:bg-bg-tertiary"
                                }`}
                            >
                                <PhotoIcon className="h-5 w-5 shrink-0" />
                                Выберите, перетащите или вставьте (Ctrl+V) · до {MAX_IMAGE_MB} МБ · лучше 16:9
                            </button>
                        )}
                    </div>

                    {error && (
                        <div role="alert" className="flex items-start gap-2 rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2.5 text-sm text-rose-500">
                            <ExclamationTriangleIcon className="h-5 w-5 shrink-0" />
                            {error}
                        </div>
                    )}

                    {confirming ? (
                        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3">
                            <p className="text-sm text-text-primary">
                                <b>Отправить push-уведомление всем пользователям?</b> Отменить рассылку после отправки нельзя.
                            </p>
                            <div className="mt-3 flex gap-2">
                                <button
                                    type="submit"
                                    autoFocus
                                    disabled={sending}
                                    className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-amber-500 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-600 disabled:opacity-60"
                                >
                                    {sending ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <PaperAirplaneIcon className="h-4 w-4" />}
                                    {sending ? "Отправка…" : "Да, отправить всем"}
                                </button>
                                <button
                                    type="button"
                                    disabled={sending}
                                    onClick={() => setConfirming(false)}
                                    className="rounded-lg border border-border-primary px-4 py-2 text-sm font-medium text-text-secondary hover:bg-bg-tertiary"
                                >
                                    Отмена
                                </button>
                            </div>
                        </div>
                    ) : (
                        <button
                            type="submit"
                            disabled={!canSend}
                            className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-accent-grad px-4 py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            <PaperAirplaneIcon className="h-4 w-4" />
                            Отправить уведомление
                        </button>
                    )}
                </form>

                {/* Preview */}
                <div className="space-y-4 xl:col-span-2">
                    <section className="rounded-block border border-border-primary bg-bg-card p-5 shadow-sm">
                        <h3 className="mb-3 flex items-center justify-between text-sm font-semibold text-text-primary">
                            Push на телефоне
                            <span className="text-xs font-normal text-text-muted">язык: {lang === "kk" ? "KZ" : lang.toUpperCase()}</span>
                        </h3>
                        <div className="rounded-2xl bg-bg-primary p-3">
                            <div className="flex items-start gap-3 rounded-xl bg-bg-card p-3 shadow-sm ring-1 ring-border-primary">
                                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#147F26] text-white">
                                    <BellIcon className="h-5 w-5" />
                                </span>
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-baseline justify-between gap-2">
                                        <span className="text-[11px] font-semibold uppercase tracking-wide text-text-muted">Agroland</span>
                                        <span className="text-[11px] text-text-muted">сейчас</span>
                                    </div>
                                    <p className="truncate text-sm font-semibold text-text-primary">{previewTitle}</p>
                                    <p className="line-clamp-2 text-sm text-text-secondary">{previewText}</p>
                                </div>
                            </div>
                        </div>
                    </section>

                    <section className="rounded-block border border-border-primary bg-bg-card p-5 shadow-sm">
                        <h3 className="mb-3 flex items-center justify-between text-sm font-semibold text-text-primary">
                            В разделе «Уведомления»
                            <span className={`rounded-md px-2 py-0.5 text-xs font-medium ${typeMeta.tone}`}>{typeMeta.label}</span>
                        </h3>
                        <div className="overflow-hidden rounded-xl bg-bg-primary ring-1 ring-border-primary">
                            {image ? (
                                <img src={image.preview} alt="" className="aspect-video w-full object-cover" />
                            ) : (
                                <div className="flex aspect-video w-full items-center justify-center text-text-muted">
                                    <PhotoIcon className="h-10 w-10 opacity-40" />
                                </div>
                            )}
                            <div className="p-4">
                                <p className="text-base font-semibold text-text-primary">{previewTitle}</p>
                                <p className="mt-1 whitespace-pre-wrap break-words text-sm text-text-secondary">{previewText}</p>
                                <p className="mt-2 text-xs text-text-muted">{new Date().toLocaleDateString("ru-RU", { day: "numeric", month: "long" })}</p>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
}

export default AddNotificationForm;
